import { useDispatch, useSelector } from 'react-redux';
import {
  closeTakeover,
  getJourneyVisitorCounts,
  getOpenedNodeId,
  getWorkflow,
  openTakeover,
  selectNodes,
} from 'modules/workflows/ducks';
import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import { useWorkflowContext } from 'modules/workflows/context';
import { CountPanel, CountText, CountValue, VisitorCountText } from '../styled';
import { ContactCount } from './ContactCounts.constants';
import { getContactCountsJVC, getDefaultContactCountsJVC } from './ContactCounts.utils';
import styled from 'styled-components';
import { caption, colors, fontSizes, spacing } from 'ui/theme';
import { BreakdownType } from '../../takeovers/JourneyVisitorBreakdownTakeover/JourneyVisitorBreakdown.constant';
import { Link } from 'ui/links';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { SettingsTab } from 'modules/journey/components/SettingsTakeover/SettingsTakeover.constants';
import { TakeoverType } from '../../takeovers';
import { LinkAction } from 'modules/workflows/workflow.style';

export const CONTACT_COUNTS_TEST_ID = 'contact-counts';
export const IN_PROGRESS_VALUE_TEST_ID = `${ContactCount.InProgress}-count`;
export const NO_JVT_INFORMATIVE_MESSAGE_TEST_ID = 'no-jvt-informative-message';

const StyledDateRangeSelectText = styled.div`
  ${caption};
  color: ${colors.secondaryText};
  margin-top: ${spacing.base};
  gap: ${spacing.half}
`;

const StyledLink = styled(Link)`
  font-size: ${fontSizes.s12}
`;

const DateRangeSelectedText = ({
  showJVTInformativeMessage,
}: { showJVTInformativeMessage: boolean }) => {
  const dispatch = useDispatch();
  const canDraftMessaging = useSelector((state) =>
    selectDoesUserHaveRbacPermission(state as any)(PROJECT_PERMISSIONS.DraftMessaging),
  );
  const openedNodeId = useSelector(getOpenedNodeId);
  const openSettingsTakeover = () =>
    dispatch(
      openTakeover(TakeoverType.Settings, {
        focusedTab: SettingsTab.About,
        canDraftMessaging,
        isOverlay: !openedNodeId,
        onClose: () => dispatch(closeTakeover(TakeoverType.Settings)),
      }),
    );
  return showJVTInformativeMessage ? (
    <StyledDateRangeSelectText data-test={NO_JVT_INFORMATIVE_MESSAGE_TEST_ID}>
      No user traffic data available for your selected date range. Start collecting data by turning
      on <LinkAction onClick={openSettingsTakeover}>journey event tracking.</LinkAction>
    </StyledDateRangeSelectText>
  ) : (
    <StyledDateRangeSelectText>
      Showing user traffic data since journey event tracking was turned on.{' '}
      <StyledLink
        target="_blank"
        rel="noopener noreferrer"
        hasOpenNewTabIcon
        href="https://support.iterable.com/hc/articles/10044063369492#tracking-journey-events"
      >
        Learn more.
      </StyledLink>
    </StyledDateRangeSelectText>
  );
};
/**
 * We need to Combine ContactCounts and ContactCountsJVC into a single compoenent that's wrapped in a HOC that fetches
 * the correct data and passes into the component via a shared interface.  Originally the Contactcounts component was going to be
 * deprecated but after talking with product, we are keeping it as is.
 */

export const ContactCountsJVC = ({
  openJourneyVisitorBreakdown,
}: {
  openJourneyVisitorBreakdown: (breakdownType: BreakdownType, numUserTotal?: number) => () => void;
}) => {
  const dispatch = useDispatch();
  const workflow = useSelector(getWorkflow);
  const hasJVTEvents = (workflow?.visitorsEntered || 0) > 0;
  const isJvtOn = workflow.visitorTrackingEnabled;

  const { setShowExitSlideover } = useWorkflowContext();
  const journeyVisitorCounts = useSelector(getJourneyVisitorCounts);

  const handleOpenExitRulesDetails = () => {
    dispatch(selectNodes([]));
    setShowExitSlideover(true);
  };

  const showJVTInformativeMessage = !isJvtOn && journeyVisitorCounts.visitorsEntered === 0;

  const filteredAnalytics = showJVTInformativeMessage
    ? getDefaultContactCountsJVC()
    : getContactCountsJVC(
        journeyVisitorCounts,
        handleOpenExitRulesDetails,
        openJourneyVisitorBreakdown,
      );

  return (
    <CountPanel data-test={CONTACT_COUNTS_TEST_ID}>
      {filteredAnalytics.map((count) => {
        const hasOnClick = typeof count.onClick === 'function' && hasJVTEvents;
        return (
          <VisitorCountText key={count.key}>
            <CountText>{count.text}</CountText>
            <CountValue
              hasOnClick={hasOnClick}
              onClick={() => count.onClick && hasJVTEvents && count.onClick()}
              data-test={count.key}
            >
              {count.value}
            </CountValue>
          </VisitorCountText>
        );
      })}
      <DateRangeSelectedText showJVTInformativeMessage={showJVTInformativeMessage} />
    </CountPanel>
  );
};
